import type { Alert, AlertFactory } from '@/features/shell'
import { type OpportunityOffer } from './OpportunityOffer'
import { match } from '@/utils/pattern-match-util'
import { OfferModalViewModel } from './OfferModalViewModel'
import { formattedTimeUntil } from '@/utils/date-util'
import { type OpportunityOfferStore } from './OpportunityOfferStore'
import { type ConsultationModalViewModel } from '@/features/opportunities/ConsultationModal/ConsultationModalViewModel'
import type { PickupConsultationJobFn } from '@/features/jobs'
import { action, computed, observable, runInAction } from 'mobx'
import { filter, share, switchMap, take, tap, map } from 'rxjs/operators'
import { tapAction, toStream } from '@/utils/mobx-util'
import { merge } from 'rxjs'
import { filterNonNil, shareLatest } from '@/utils/rx-util'
import type { NavigateFn } from '@/RootStore'
import type { SystemNotification } from '@/features/notifications'
import { task } from 'mobx-task'
import { orderBy } from 'lodash-es'
import type { GetCurrentMemberFn } from '@/features/iam'

interface Accept {
  type: 'Accept'
  id: string
}
interface Decline {
  type: 'Decline'
  id: string
}

/**
 * The automated action to take.
 */
export type AutomatedAction = Accept | Decline

/**
 * View Model for handling the general opportunity offer acceptance and rejection
 * process. Manages the realtime stream of offers and removals and when the offer
 * toast should be displayed and hidden.
 */
export class OpportunityOffersViewModel {
  /**
   * View Models that are actively being shown.
   */
  @observable
  private accessor _offerModalViewModels: OfferModalViewModel[] = []

  /**
   * View Models that are buffered behind a toast and not yet shown.
   */
  @observable
  private accessor _bufferedViewModels: OfferModalViewModel[] = []

  /**
   * The currently excluded offer. This is an offer that has been designated
   * by some external caller as to be excluded from regular handling - it won't
   * cause the toast to show.
   */
  @observable
  private accessor _excludedOffer: OpportunityOffer | null = null

  /**
   * A toast that indicates that jobs have been offered. Associated with the
   * toast is a "batch" of buffered offers behind it.
   */
  private _offerToast: Alert | null = null

  /**
   * Emits a signal when the buffered offers becomes empty.
   *
   * @private
   */
  private readonly _offersBufferEmptied$ = toStream(
    () => this._bufferedViewModels.length,
    true,
  ).pipe(
    // Check whether the buffer is empty based on it's length
    map((len) => len === 0),
    // We only want to be notified when it's empty.
    filter(Boolean),
    // Emit latest on subscribe.
    shareLatest(),
  )

  /**
   * When an offer is received, show a notification.
   */
  readonly systemNotifications$

  constructor(
    private readonly _opportunityOfferStore: OpportunityOfferStore,
    private readonly _consultationModalViewModel: ConsultationModalViewModel,
    private readonly _pickupConsultationJob: PickupConsultationJobFn,
    private readonly _newAlert: AlertFactory,
    private readonly _navigate: NavigateFn,
    private readonly _getCurrentMember: GetCurrentMemberFn,
  ) {
    this.systemNotifications$ =
      this._opportunityOfferStore.incomingOffers$.pipe(
        map((offer): SystemNotification => {
          return {
            icon: offer.workspace.branding.logos.icon,
            // Since we auto-close the notification when the offer expires anyway,
            // we can set a high timeout.
            timeout: 999999999,
            // NOTE: These won't be reactive but that's fine.
            title: this._toastTitle(),
            body: this._toastDescription(),
            // When the offers buffer is emptied, close the notification.
            close$: this._offersBufferEmptied$,
            onClick: () => this._showBufferedOffers(),
          }
        }),
      )

    // Emits everytime an offer is marked as "excluded".
    const offerExclusions$ = toStream(() => this._excludedOffer, true).pipe(
      filterNonNil(),
      share(),
    )

    // Keeps the offer modals and the offer toast in sync with incoming and
    // removed offers for the app's lifetime, so offers show on any page.
    merge(
      // Handles resetting the exclusions once the excluded offer has been removed.
      offerExclusions$.pipe(
        switchMap((o) =>
          _opportunityOfferStore.offersRemoved$.pipe(
            filter((rem) => o.id === rem.id),
            take(1),
            tapAction(this.resetExclusions),
          ),
        ),
      ),
      // Update the VM collection for each incoming offer.
      _opportunityOfferStore.incomingOffers$.pipe(
        filter((o) => this._excludedOffer?.id !== o.id),
        tapAction((offer) => {
          this._bufferedViewModels.push(this._createVm(offer))

          this._offerToast ??= this._setupOfferToast()
          this._offerToast.show()
        }),
      ),
      // Deflate the modal when its offer is removed.
      _opportunityOfferStore.offersRemoved$.pipe(tap(this._closeOfferModals)),
      // Update the VM collection and dismiss the toast when there are no offers.
      merge(_opportunityOfferStore.offersRemoved$, offerExclusions$).pipe(
        tap(this._handleOpportunityOfferRemoved),
      ),
    ).subscribe()
  }

  /**
   * The Offer Modal View Models to display.
   */
  @computed
  get offerModalViewModels() {
    return this._offerModalViewModels
  }

  /**
   * Handles the automatic acceptance or rejection of an offer.
   */
  handleOfferResponse = task.resolved(async (action: AutomatedAction) => {
    const offer = await this._opportunityOfferStore.fetchOpportunityOffer(
      action.id,
    )

    this.excludeOffer(offer)

    const vm = this._createVm(offer)

    runInAction(() => {
      vm.modal.show()
      this._offerModalViewModels = [vm]
    })

    switch (action.type) {
      case 'Accept':
        await vm.acceptOpportunity()
        break
      case 'Decline':
        await vm.declineOpportunity()
        break
    }
  })

  /**
   * Permits marking an offer as to be excluded.
   */
  @action.bound
  excludeOffer(offer: OpportunityOffer) {
    this._excludedOffer = offer
  }

  /**
   * Removes offer exclusions.
   */
  @action.bound
  resetExclusions() {
    this._excludedOffer = null
  }

  /**
   * Closes the showing modals of a removed offer.
   */
  @action.bound
  private _closeOfferModals(offer: OpportunityOffer) {
    this._offerModalViewModels
      .filter((vm) => vm.id === offer.id && vm.modal.showing)
      .forEach((vm) => vm.close())
  }

  /**
   * Handler for when opportunity offers are removed.
   */
  @action.bound
  private _handleOpportunityOfferRemoved(offer: OpportunityOffer) {
    this._bufferedViewModels = this._bufferedViewModels.filter(
      (vm) => vm.id !== offer.id,
    )

    this._offerModalViewModels = this._offerModalViewModels.filter(
      (vm) => vm.id !== offer.id,
    )

    // Dismiss the toast if we no longer have any buffered view models.
    if (!this._bufferedViewModels.length) {
      this._offerToast?.dismiss()
      this._offerToast = null
    }
  }

  /**
   * Shows the buffered offers and clears the buffer.
   */
  @action.bound
  private _showBufferedOffers() {
    this._offerModalViewModels = this.offerModalViewModels.concat(
      sortedByPriority(this._bufferedViewModels),
    )

    this._bufferedViewModels = []

    this._offerModalViewModels.forEach((vm) => vm.modal.show())
    this._offerToast?.dismiss()
    this._offerToast = null
  }

  /**
   * Configures the offer toast with its interaction handler and reactive
   * title and description.
   */
  private _setupOfferToast() {
    return this._newAlert()
      .withState('success')
      .saying(this._toastTitle, this._toastDescription)
      .onInteract(this._showBufferedOffers)
      .withActions({
        title: 'Review',
        handler: this._showBufferedOffers,
      })
      .cancelAutoDismiss()
  }

  /**
   * Reactive getter for the toast title.
   */
  private readonly _toastTitle = () => {
    return this._bufferedViewModels.length > 1
      ? `Congrats, you scored ${this._bufferedViewModels.length} jobs!`
      : 'Congrats, you scored a job!'
  }

  /**
   * Reactive getter for the toast description.
   */
  private readonly _toastDescription = () => {
    if (this._bufferedViewModels.length === 0) {
      return ''
    }

    if (this._bufferedViewModels.length > 1) {
      const highestPriorityVm = orderBy(
        this._bufferedViewModels,
        (vm) => vm.expirationMillis,
      )[0]

      const actionPhrase = match(highestPriorityVm.offerExpirationAction, {
        Accept: () => 'auto-accept',
        Decline: () => 'auto-decline',
      })

      return `Next ${actionPhrase} in ${formattedTimeUntil(
        highestPriorityVm.timeUntilExpiration,
      )}`
    }

    const actionPhrase = match(
      this._bufferedViewModels[0].offerExpirationAction,
      {
        Accept: () => 'Auto-accepting',
        Decline: () => 'Auto-declining',
      },
    )

    return `${actionPhrase} in ${formattedTimeUntil(
      this._bufferedViewModels[0].timeUntilExpiration,
    )}`
  }

  /**
   * Helper method for constructing Offer Modal View Models via Partial Application.
   */
  private _createVm(offer: OpportunityOffer) {
    return new OfferModalViewModel(
      this._opportunityOfferStore,
      offer,
      this._consultationModalViewModel,
      this._pickupConsultationJob,
      this._navigate,
      this._newAlert,
      this._getCurrentMember,
    )
  }
}

/**
 * Sorts a list of view models by their expiration millis in descending order.
 * Descending order is used so that the offer that will expire soonest is at the
 * end, thus allowing its modal to be drawn over all the others.
 */
function sortedByPriority(vms: OfferModalViewModel[]) {
  return orderBy(vms, (vm) => vm.expirationMillis, 'desc')
}
